import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navbar, Footer } from "../components/Layout";
import { useNoIndex } from "../hooks/useNoIndex";
import { Send, Sparkles, Loader2, Crown, Lock } from "lucide-react";

const STARTERS = [
  "Différence entre は et が ?",
  "Comment se présenter poliment ?",
  "Donne-moi un exercice de hiragana",
  "Que signifie いただきます ?",
];

const Tutor = () => {
  useNoIndex();
  const { user, loading: authLoading, refresh } = useAuth();
  const [params] = useSearchParams();
  // Scope strict — actif uniquement pour la leçon témoin l1.
  const lessonId = params.get("lesson") === "l1" ? "l1" : null;
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [session, setSession] = useState(null);
  const [sending, setSending] = useState(false);
  const [count, setCount] = useState({ used: 0, limit: 5 });
  const [showUpgrade, setShowUpgrade] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);
  useEffect(() => {
    if (user) setCount({ used: user.ai_messages_today || 0, limit: user.ai_daily_limit || 5 });
  }, [user]);

  if (authLoading) return <div className="washi-bg min-h-screen"><Navbar /></div>;
  if (!user) return <Navigate to="/login" replace />;

  const isPremium = user.is_premium;
  const remaining = Math.max(0, count.limit - count.used);
  const quotaExceeded = !isPremium && remaining === 0;

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    if (quotaExceeded) { setShowUpgrade(true); return; }
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/chat", { message: msg, session_id: session, lesson_id: lessonId });
      setSession(data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
      setCount({ used: data.ai_messages_today, limit: data.ai_daily_limit });
      // Refresh user state in background
      refresh().catch(() => {});
    } catch (err) {
      if (err.response?.status === 402) {
        setShowUpgrade(true);
        setCount((c) => ({ ...c, used: c.limit }));
      } else {
        setMessages((m) => [...m, { role: "assistant", text: "Désolé, une erreur s'est produite. Réessayez ?" }]);
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="washi-bg min-h-screen flex flex-col" data-testid="tutor-page">
      <Navbar />
      <div className="max-w-5xl w-full mx-auto px-6 sm:px-12 py-10 flex-1 flex flex-col">
        <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#E94A3F] mb-2">Sensei IA · Claude Sonnet</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[1] text-[#1A2A40]">
              Posez votre question, <span className="font-jp text-[#E94A3F]">先生</span>.
            </h1>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="inline-flex items-center gap-2 bg-[#8CA07A]/20 border border-[#8CA07A] rounded-full px-3 py-1.5">
              <span className="w-2 h-2 rounded-full bg-[#8CA07A] animate-pulse" />
              <span className="text-xs font-bold text-[#1A2A40]">En ligne</span>
            </div>
            {isPremium ? (
              <div className="inline-flex items-center gap-1.5 bg-[#FFD166] text-[#1A2A40] rounded-full px-3 py-1 text-xs font-bold uppercase tracking-widest" data-testid="ai-quota-premium">
                <Crown className="w-3 h-3" /> Illimité
              </div>
            ) : (
              <div className="text-xs text-[#4A5568] font-bold" data-testid="ai-quota-counter">
                {remaining}/{count.limit} messages gratuits aujourd'hui
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 bg-[#FDFBF7] rounded-3xl border-2 border-[#1A2A40] shadow-brut p-6 sm:p-8 flex flex-col min-h-[500px]" data-testid="chat-window">
          <div className="flex-1 overflow-y-auto space-y-4 mb-6 pr-2" style={{ maxHeight: "55vh" }}>
            {messages.length === 0 && (
              <div className="text-center py-10">
                <div className="inline-flex w-16 h-16 rounded-2xl bg-[#E94A3F] items-center justify-center mb-4 shadow-brut-sm">
                  <Sparkles className="w-7 h-7 text-white" />
                </div>
                <p className="text-[#4A5568] mb-6">Démarrez par une question, ou choisissez ci-dessous :</p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {STARTERS.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => send(s)}
                      data-testid={`starter-${i}`}
                      className="bg-[#F3F6F1] hover:bg-[#FFD166] border border-[#1A2A40]/10 rounded-full px-4 py-2 text-sm font-medium text-[#1A2A40] transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`msg-${i}`}>
                <div className={`max-w-[85%] rounded-2xl px-5 py-3 leading-relaxed whitespace-pre-wrap text-sm sm:text-base ${
                  m.role === "user"
                    ? "bg-[#1A2A40] text-white rounded-br-sm"
                    : "bg-[#8CA07A]/15 border border-[#8CA07A]/30 text-[#1A2A40] rounded-bl-sm"
                }`}>
                  {m.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start" data-testid="thinking-indicator">
                <div className="bg-[#8CA07A]/15 border border-[#8CA07A]/30 rounded-2xl px-5 py-3 inline-flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-[#8CA07A]" />
                  <span className="text-sm text-[#4A5568]">Sensei réfléchit...</span>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {quotaExceeded ? (
            <div className="bg-[#1A2A40] text-white rounded-2xl p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="quota-banner">
              <div className="flex items-center gap-3">
                <Lock className="w-5 h-5 text-[#FFD166] shrink-0" />
                <div>
                  <div className="font-bold">Limite gratuite atteinte aujourd'hui</div>
                  <div className="text-sm text-white/70">Passez Premium pour un Sensei IA illimité.</div>
                </div>
              </div>
              <Link to="/pricing" data-testid="quota-upgrade-btn" className="bg-[#E94A3F] font-bold py-3 px-5 rounded-xl shadow-brut-sm inline-flex items-center gap-2">
                <Crown className="w-4 h-4" /> Voir les plans
              </Link>
            </div>
          ) : (
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-3" data-testid="chat-form">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Posez votre question en français..."
                data-testid="chat-input"
                className="flex-1 bg-[#FDFBF7] border-2 border-[#1A2A40]/20 rounded-xl px-4 py-3 focus:outline-none focus:border-[#E94A3F]"
                disabled={sending}
              />
              <button
                type="submit"
                disabled={sending || !input.trim()}
                data-testid="chat-send-btn"
                className="bg-[#E94A3F] text-white font-bold px-5 rounded-xl shadow-brut-sm hover-lift inline-flex items-center gap-2 disabled:opacity-60"
              >
                <Send className="w-4 h-4" /> Envoyer
              </button>
            </form>
          )}
        </div>
      </div>

      {showUpgrade && (
        <div className="fixed inset-0 bg-[#1A2A40]/70 backdrop-blur-sm z-50 flex items-center justify-center p-6" data-testid="upgrade-modal">
          <div className="bg-[#FDFBF7] rounded-3xl p-8 sm:p-10 max-w-md w-full border-2 border-[#1A2A40] shadow-brut-lg">
            <div className="inline-flex w-14 h-14 rounded-2xl bg-[#E94A3F] items-center justify-center mb-4">
              <Crown className="w-7 h-7 text-white" />
            </div>
            <h2 className="font-display font-black text-2xl text-[#1A2A40] mb-2">Sensei IA, sans limite.</h2>
            <p className="text-[#4A5568] mb-6">
              Vous avez utilisé vos 5 messages gratuits du jour. Passez Premium pour un accès illimité, essai 3j offert.
            </p>
            <div className="flex gap-3">
              <Link to="/pricing" data-testid="modal-upgrade-btn" className="flex-1 bg-[#E94A3F] text-white font-bold py-3 rounded-xl shadow-brut-sm text-center">
                Voir les plans
              </Link>
              <button onClick={() => setShowUpgrade(false)} data-testid="modal-close-btn" className="px-5 text-[#4A5568] hover:text-[#1A2A40] font-medium">
                Plus tard
              </button>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
};

export default Tutor;
